import React, { useState, useEffect, useCallback } from 'react';
import Mermaid from './Mermaid';
import { IconClose } from './Icons';
import './DecisionGraphPanel.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

const STATUS_LABELS = {
  active: 'Active',
  revisited: 'Revisited',
  reversed: 'Reversed',
  superseded: 'Superseded',
};

function authHeaders() {
  const token = localStorage.getItem('symphonate-token');
  return { Authorization: `Bearer ${token}` };
}

/**
 * Workspace-level Decision Graph — the visible manifestation of the durability
 * moat. Renders every decision as a Mermaid flowchart (grouped by thread,
 * coloured by status), lists the decisions with jump-to-thread links, and lets
 * members curate the relationships between them (depends-on / blocks /
 * supersedes / relates-to).
 */
export default function DecisionGraphPanel({
  workspaceId,
  workspaceName,
  onClose,
  onJumpToThread,
}) {
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add-link form state.
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [linkType, setLinkType] = useState('depends-on');
  const [linkError, setLinkError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${SERVER_URL}/api/workspaces/${workspaceId}/decision-graph`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load decision graph');
      setGraph(await res.json());
    } catch (e) {
      setError(e.message || 'Failed to load decision graph');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const nodes = graph?.nodes || [];
  const edges = graph?.edges || [];
  const linkTypes = graph?.linkTypes || [];
  const decisionById = React.useMemo(() => {
    const m = {};
    for (const n of nodes) m[n.id] = n;
    return m;
  }, [nodes]);

  const handleAddLink = async (e) => {
    e.preventDefault();
    setLinkError(null);
    if (!fromId || !toId) {
      setLinkError('Pick two decisions to link.');
      return;
    }
    if (fromId === toId) {
      setLinkError('A decision cannot link to itself.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/workspaces/${workspaceId}/decision-links`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ fromDecisionId: fromId, toDecisionId: toId, type: linkType }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setLinkError(data.error || 'Could not add link.');
        return;
      }
      if (data.graph) setGraph(data.graph);
      setFromId('');
      setToId('');
    } catch {
      setLinkError('Could not add link.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveLink = async (linkId) => {
    setBusy(true);
    try {
      const res = await fetch(
        `${SERVER_URL}/api/workspaces/${workspaceId}/decision-links/${linkId}`,
        { method: 'DELETE', headers: authHeaders() },
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.graph) setGraph(data.graph);
    } finally {
      setBusy(false);
    }
  };

  // Download the workspace decision log as a portable file. Fetched with the
  // auth header (a plain <a href> can't carry the bearer token), then turned
  // into a blob and saved via a synthetic link click.
  const handleExport = async (format) => {
    setExporting(format);
    try {
      const res = await fetch(
        `${SERVER_URL}/api/workspaces/${workspaceId}/decisions/export.${format}`,
        { headers: authHeaders() },
      );
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `decisions.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Could not export decisions.');
    } finally {
      setExporting(null);
    }
  };

  // Group decisions by their source thread for the list view.
  const byThread = React.useMemo(() => {
    const groups = [];
    const index = {};
    for (const n of nodes) {
      if (!(n.roomId in index)) {
        index[n.roomId] = groups.length;
        groups.push({ roomId: n.roomId, roomName: n.roomName, decisions: [] });
      }
      groups[index[n.roomId]].decisions.push(n);
    }
    return groups;
  }, [nodes]);

  return (
    <div className="dgraph-backdrop" onClick={onClose} role="presentation">
      <div
        className="dgraph-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Decision graph"
      >
        <div className="dgraph-header">
          <div>
            <h2>Decision Graph</h2>
            <p className="dgraph-subtitle">{workspaceName}</p>
          </div>
          <div className="dgraph-header-actions">
            <button
              className="dgraph-export-btn"
              onClick={() => handleExport('csv')}
              disabled={!!exporting}
              title="Export the decision log as CSV"
            >
              {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}
            </button>
            <button
              className="dgraph-export-btn"
              onClick={() => handleExport('json')}
              disabled={!!exporting}
              title="Export the decision log as JSON"
            >
              {exporting === 'json' ? 'Exporting…' : 'Export JSON'}
            </button>
            <button className="dgraph-close" onClick={onClose} aria-label="Close decision graph">
              <IconClose size={18} />
            </button>
          </div>
        </div>

        {loading && <div className="dgraph-empty">Loading decision graph…</div>}
        {error && !loading && <div className="dgraph-empty dgraph-error">{error}</div>}

        {!loading && !error && graph && (
          <div className="dgraph-body">
            {nodes.length === 0 ? (
              <div className="dgraph-empty">
                No decisions logged yet. As your team logs decisions in threads, they’ll appear here
                — and you can map how they connect.
              </div>
            ) : (
              <>
                <div className="dgraph-stats">
                  <span>
                    <strong>{graph.stats.total}</strong> decisions
                  </span>
                  <span>
                    <strong>{graph.stats.edgeCount}</strong> links
                  </span>
                  <span>
                    <strong>{graph.stats.threadCount}</strong> threads
                  </span>
                </div>

                {graph.mermaid && (
                  <div className="dgraph-canvas">
                    <Mermaid content={graph.mermaid} className="dgraph-mermaid" />
                  </div>
                )}

                <div className="dgraph-legend">
                  <span className="legend-item legend-active">Active</span>
                  <span className="legend-item legend-revisited">Revisited</span>
                  <span className="legend-item legend-reversed">Reversed</span>
                </div>

                {/* Add-link control */}
                <form className="dgraph-linkform" onSubmit={handleAddLink}>
                  <span className="dgraph-linkform-label">Link decisions:</span>
                  <select value={fromId} onChange={(e) => setFromId(e.target.value)}>
                    <option value="">From…</option>
                    {nodes.map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.title}
                      </option>
                    ))}
                  </select>
                  <select value={linkType} onChange={(e) => setLinkType(e.target.value)}>
                    {linkTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <select value={toId} onChange={(e) => setToId(e.target.value)}>
                    <option value="">To…</option>
                    {nodes.map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.title}
                      </option>
                    ))}
                  </select>
                  <button type="submit" className="dgraph-addbtn" disabled={busy}>
                    Add
                  </button>
                </form>
                {linkError && <div className="dgraph-linkerror">{linkError}</div>}

                {/* Existing links */}
                {edges.length > 0 && (
                  <div className="dgraph-links">
                    <h3>Links</h3>
                    <ul>
                      {edges.map((edge) => {
                        const from = decisionById[edge.fromDecisionId];
                        const to = decisionById[edge.toDecisionId];
                        return (
                          <li key={edge.id}>
                            <span className="dgraph-link-text">
                              <strong>{from?.title || 'Unknown'}</strong>
                              <em className="dgraph-link-type"> {edge.label} </em>
                              <strong>{to?.title || 'Unknown'}</strong>
                            </span>
                            <button
                              className="dgraph-unlink"
                              onClick={() => handleRemoveLink(edge.id)}
                              disabled={busy}
                              aria-label="Remove link"
                              title="Remove link"
                            >
                              ×
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* Decisions grouped by thread */}
                <div className="dgraph-decisions">
                  <h3>Decisions by thread</h3>
                  {byThread.map((group) => (
                    <div key={group.roomId} className="dgraph-thread">
                      <button
                        className="dgraph-thread-name"
                        onClick={() => onJumpToThread?.(group.roomId)}
                        title="Open this thread"
                      >
                        #{group.roomName}
                      </button>
                      <ul>
                        {group.decisions.map((d) => (
                          <li key={d.id} className={`dgraph-decision status-${d.status}`}>
                            <span className={`dgraph-status-dot status-${d.status}`} />
                            <span className="dgraph-decision-title">{d.title}</span>
                            <span className="dgraph-decision-status">
                              {STATUS_LABELS[d.status] || d.status}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
